import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Alert, Box, Button, Grid, IconButton, InputAdornment, TextField, Typography } from "@mui/material";

import DownloadIcon from "@mui/icons-material/Download";
import SyncIcon from "@mui/icons-material/Sync";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";
import SearchIcon from "@mui/icons-material/Search";

import { useDevice } from "../utils/DeviceProvider";
import TitledCard from "./templates/basic/TitledCard";
import { useApi } from "../utils/ApiProvider";
import { saveBlobToFile } from "../utils/utils";
import { useMutation } from "react-query";
import { useErrorHandler } from "../hooks/useErrorHandler";
import B64FileField from "./B64FileField";
import { Form } from "react-final-form";
import { ConfigImportResponse, ImportedConfig } from "../utils/types";
import { UnitSelectorWithFullSearchDialog } from "../pages/UnitSelectorWithFullSearchDialog";

export type ImportForm = {
  importFile: string;
};

export type ImportMessage =
  | ImportedConfig["warnings"][0]
  | { type: "import-loading" }
  | { type: "imported"; withWarnings: boolean }
  | { type: "not-found" }
  | { type: "error"; message: string };

export const ExportImport = ({ loadConfig }: { loadConfig: (olderConfig: any) => void }) => {
  const {
    device: { unitid },
  } = useDevice();
  const api = useApi();
  const [importFile, setImportFile] = useState<string | null>(null);
  const [importMessages, setImportMessages] = useState<ImportMessage[]>([]);
  const [importFromUnitMessages, setImportFromUnitMessages] = useState<ImportMessage[]>([]);
  const [unitSelectDialogOpen, setUnitSelectDialogOpen] = useState(false);
  const [importSource, setImportSource] = useState("");

  const { formatMessage } = useIntl();

  const { errorHandler: onError } = useErrorHandler([[{ status: 400 }, () => null]]);
  const exportQuery = useCallback(
    () => api(`/units/export`, { method: "post", headers: { "Content-Type": "application/json" }, body: JSON.stringify([unitid]) }),
    [api, unitid]
  );
  const onExportSuccess = useCallback(
    (response) => {
      saveBlobToFile(response, `${unitid}`);
    },
    [unitid]
  );
  const { mutate: exportConfig, isLoading: exportLoading } = useMutation({ mutationFn: exportQuery, onSuccess: onExportSuccess, onError });
  const importQuery = useCallback(
    (importFile: string) => {
      setImportMessages([{ type: "import-loading" }]);
      return api<ConfigImportResponse>(`/units/import`, {
        method: "post",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ importFile }),
      });
    },
    [api]
  );
  const onImportSuccess = useCallback(
    (response: ConfigImportResponse) => {
      if (!response?.[unitid]) {
        setImportMessages([{ type: "not-found" }]);
        return;
      }
      loadConfig(response[unitid].config);
      setImportMessages([...response[unitid].warnings, { type: "imported", withWarnings: response[unitid].warnings.length > 0 }]);
    },
    [loadConfig, unitid]
  );
  const { mutate: importConfig, isLoading: importLoading } = useMutation({ mutationFn: importQuery, onSuccess: onImportSuccess, onError });
  const importFromUnitQuery = useCallback(() => {
    setImportFromUnitMessages([{ type: "import-loading" }]);
    return api<ImportedConfig>(`/units/import/fromunit`, {
      method: "post",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ source: parseInt(importSource), target: unitid }),
    });
  }, [api, importSource, unitid]);
  const onImportFromUnitSuccess = useCallback(
    (response: ImportedConfig) => {
      loadConfig(response.config);
      setImportFromUnitMessages([...response.warnings, { type: "imported", withWarnings: response.warnings.length > 0 }]);
    },
    [loadConfig]
  );
  const onImportFromUnitError = useCallback((err: any) => {
    let message = (err.data?.error ?? err.message ?? err) as string;
    if (err.status === 401 && err.data.type === 'unit') {
      message = formatMessage({defaultMessage: "You have no rights for this unit: {unitid}."}, {unitid});
    } else if (err.data.error === `Template is not found for unit ${unitid}.`) {
      message = formatMessage({defaultMessage: "Template is not found for unit {unitid}."}, {unitid});
    } else if (err.data.error === `Template is not found for unit ${importSource}.`) {
      message = formatMessage({defaultMessage: "Template is not found for unit {unitid}."}, {unitid: importSource});
    } else if (err.data.error === `Config is not found for unit ${unitid}.`) {
      message = formatMessage({defaultMessage: "Config is not found for unit {unitid}."}, {unitid});
    }
    setImportFromUnitMessages([{ type: "error", message }]);
  }, [unitid, importSource, formatMessage]);
  const { mutate: importConfigFromUnit, isLoading: importFromUnitLoading } = useMutation({
    mutationFn: importFromUnitQuery,
    onSuccess: onImportFromUnitSuccess,
    onError: onImportFromUnitError,
  });
  const importFromUnitDisabled = useMemo(() => importFromUnitLoading || isNaN(parseInt(importSource)), [importFromUnitLoading, importSource]);

  const onImportSubmit = useCallback(
    (formData) => {
      importConfig(formData.importFile);
    },
    [importConfig]
  );

  const openUnitSelectDialog = useCallback(() => setUnitSelectDialogOpen(true), []);
  const closeUnitSelectDialog = useCallback(() => setUnitSelectDialogOpen(false), []);

  const selectImportSource = useCallback(
    (unitid: number) => {
      closeUnitSelectDialog();
      setImportSource(`${unitid}`);
    },
    [closeUnitSelectDialog]
  );

  const unitSelectDialog = useMemo(() => {
    return (
      <UnitSelectorWithFullSearchDialog open={unitSelectDialogOpen} onClose={closeUnitSelectDialog} onSelect={({ unitid }) => selectImportSource(unitid)} />
    );
  }, [closeUnitSelectDialog, selectImportSource, unitSelectDialogOpen]);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <TitledCard title={formatMessage({ defaultMessage: "Import from other Helios ID" })}>
          <Grid container spacing={1} sx={{ p: 1, alignItems: "center" }}>
            <Grid item>
              <TextField
                label={formatMessage({ defaultMessage: "Source Helios ID" })}
                variant="filled"
                value={importSource}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" disableTypography>
                      <IconButton size="small" onClick={openUnitSelectDialog}>
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                onChange={(e) => setImportSource(e.target.value)}
                sx={{ margin: 0 }}
                size="small"
                autoComplete="off"
              />
            </Grid>
            <Grid item>
              <Button onClick={() => importConfigFromUnit()} disabled={importFromUnitDisabled}>
                {formatMessage({ defaultMessage: "Import" })}
              </Button>
            </Grid>
          </Grid>
          {importFromUnitMessages.length > 0 && (
            <Grid container spacing={1} sx={{ p: 1 }}>
              {importFromUnitMessages.map((message, index) => (
                <Grid item key={`message-${index}`} xs={12}>
                  <MessageItem message={message} />
                </Grid>
              ))}
            </Grid>
          )}
          {unitSelectDialog}
        </TitledCard>
      </Grid>
      <Grid item xs={12}>
        <TitledCard title={formatMessage({ defaultMessage: "Export" })}>
          <Box sx={{ m: 1 }}>
            <Typography>{formatMessage({ defaultMessage: "Export saved configuration to xslx format:" })}</Typography>
            <Button startIcon={<DownloadIcon />} onClick={() => exportConfig()} disabled={exportLoading}>
              {formatMessage({ defaultMessage: "Save" })}
            </Button>
          </Box>
        </TitledCard>
      </Grid>
      <Grid item xs={12}>
        <TitledCard title={formatMessage({ defaultMessage: "Import from xlsx file" })}>
          <Box sx={{ m: 1 }}>
            <Form<ImportForm>
              onSubmit={onImportSubmit}
              initialValues={{ importFile: "" }}
              render={({ handleSubmit }) => (
                <form onSubmit={handleSubmit}>
                  <B64FileField
                    name="importFile"
                    highlightDirty={false}
                    sx={{ minHeight: 120, height: 1 / 1 }}
                    disabled={importLoading}
                    onChange={setImportFile}
                  />
                  <Button type="submit" sx={{ mt: 1 }} disabled={importLoading || !importFile}>
                    {formatMessage({ defaultMessage: "Import from this file" })}
                  </Button>
                </form>
              )}
            />
          </Box>
          {importMessages.length > 0 && (
            <Grid container spacing={1} sx={{ p: 1 }}>
              {importMessages.map((message, index) => (
                <Grid item key={`message-${index}`} xs={12}>
                  <MessageItem message={message} />
                </Grid>
              ))}
            </Grid>
          )}
        </TitledCard>
      </Grid>
    </Grid>
  );
};

export const MessageItem = ({ message }: { message: ImportMessage }) => {
  const { formatMessage } = useIntl();

  if (message.type === "import-loading") {
    return (
      <Alert severity="info" icon={<RotatingSyncIcon />}>
        {formatMessage({ defaultMessage: "Import loading..." })}
      </Alert>
    );
  }

  if (message.type === "imported") {
    return (
      <Alert severity={message.withWarnings ? "info" : "success"} icon={<PublishedWithChangesIcon fontSize="inherit" />}>
        {formatMessage({ defaultMessage: "Imported." })}
      </Alert>
    );
  }

  if (message.type === "not-found") {
    return <Alert severity="error">{formatMessage({ defaultMessage: "Helios ID not found in the file." })}</Alert>;
  }

  if (message.type === "extraKey") {
    return (
      <Alert severity="warning">
        <Typography variant="subtitle2">{formatMessage({ defaultMessage: "Extra key" })}</Typography>
        <Typography sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}>
          {message.key} = {JSON.stringify(message.value, null, 2)}
        </Typography>
      </Alert>
    );
  }
  if (message.type === "format") {
    return (
      <Alert severity="warning">
        <Typography variant="subtitle2">{formatMessage({ defaultMessage: "Format difference" })}</Typography>
        <Typography>{formatMessage({ defaultMessage: "It looks like the source and the target device has different template." })}</Typography>
      </Alert>
    );
  }
  if (message.type === "company") {
    return (
      <Alert severity="warning">
        <Typography variant="subtitle2">{formatMessage({ defaultMessage: "Different owner companies" })}</Typography>
        <Typography>
          {formatMessage({ defaultMessage: "Source company" })}: {message.source}
        </Typography>
        <Typography>
          {formatMessage({ defaultMessage: "Target company" })}: {message.target}
        </Typography>
      </Alert>
    );
  }
  if (message.type === "error") {
    return <Alert severity="error">{message.message}</Alert>;
  }
  return (
    <Alert severity="warning">
      <pre>{JSON.stringify(message, null, 2)}</pre>
    </Alert>
  );
};

export const RotatingSyncIcon = () => (
  <SyncIcon
    fontSize="inherit"
    sx={{
      animation: "spin 2s linear infinite",
      "@keyframes spin": {
        "0%": {
          transform: "rotate(0deg) scaleX(-1)",
        },
        "100%": {
          transform: "rotate(360deg) scaleX(-1)",
        },
      },
    }}
  />
);
